import vi from "./locales/vi.json";
import en from "./locales/en.json";

const LOCALE_STORAGE_KEY = "admin_locale";
const SUPPORTED = ["vi", "en"] as const;

export type Locale = typeof SUPPORTED[number];

const dictionaries: Record<Locale, Record<string, unknown>> = { vi, en };

let currentLocale: Locale = "vi";
const listeners = new Set<() => void>();

const detectLocale = (): Locale => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = String(params.get("lang") || "")
      .trim()
      .toLowerCase();
    if ((SUPPORTED as readonly string[]).includes(fromQuery)) {
      return fromQuery as Locale;
    }
  } catch {
    /* ignore */
  }
  try {
    const stored = String(localStorage.getItem(LOCALE_STORAGE_KEY) || "")
      .trim()
      .toLowerCase();
    if ((SUPPORTED as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    /* ignore */
  }
  return "vi";
};

const getNested = (dict: Record<string, unknown>, path: string): string | undefined => {
  const parts = String(path || "").split(".");
  let cur: unknown = dict;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === "string" ? cur : undefined;
};

export const t = (key: string, vars: Record<string, string | number> = {}): string => {
  const dict = dictionaries[currentLocale] || dictionaries.vi;
  let text =
    getNested(dict, key) ?? getNested(dictionaries.vi, key) ?? String(key);
  Object.entries(vars || {}).forEach(([name, value]) => {
    text = text.split(`{{${name}}}`).join(String(value ?? ""));
  });
  return text;
};

export const getLocale = (): Locale => currentLocale;

export const setLocale = (locale: string): Locale => {
  const next = (SUPPORTED as readonly string[]).includes(locale)
    ? (locale as Locale)
    : "vi";
  currentLocale = next;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  try {
    const url = new URL(window.location.href);
    if (next === "vi") url.searchParams.delete("lang");
    else url.searchParams.set("lang", next);
    window.history.replaceState({}, "", url);
  } catch {
    /* ignore */
  }
  try {
    document.documentElement.lang = next;
  } catch {
    /* ignore */
  }
  listeners.forEach((listener) => listener());
  return currentLocale;
};

export const initI18n = (): Locale => {
  currentLocale = detectLocale();
  try {
    document.documentElement.lang = currentLocale;
  } catch {
    /* ignore */
  }
  return currentLocale;
};

export const subscribeLocale = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const supportedLocales: Locale[] = [...SUPPORTED];

export const localeLabels: Record<Locale, string> = {
  vi: "VI",
  en: "EN",
};
